
<template>

  <report-panel icon="$customer" :title="$t('titles.repeat_parkers')" :loading="loading">

    <v-row>

      <v-col cols="12" md="6">
        <div ref="aggregateChartContainer">
          <bar
            :chart-options="aggregateChartOptions"
            :chart-data="aggregateChartData"
            :styles="aggregateChartStyles"
          />
        </div>
      </v-col>

      <v-col cols="12" md="6">
        <div ref="breakdownChartContainer">
          <bar
            :chart-options="breakdownChartOptions"
            :chart-data="breakdownChartData"
            :styles="breakdownChartStyles"
          />
        </div>
      </v-col>

    </v-row>

  </report-panel>

</template>

<script setup lang="ts">

import { computed, ref, useTemplateRef } from 'vue'
import { computedAsync } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import ReportPanel from './ReportPanel.vue'
import { ReportingApi, type GetTransientDriversResponse } from '../../../../services/api-clients/ReportingApi'
import useFitChartToContainer from '../../../../utils/useFitChartToContainer'
import NotificationService from '../../../../services/NotificationService'
import { Temporal } from '@js-temporal/polyfill'

// ChartJS
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  type ChartData,
  type ChartOptions,
  type ChartDataset, type TooltipItem
} from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

interface Props {
  locationIds: number[]
  channels: string[]
  start: null | Temporal.PlainDate
  end: null | Temporal.PlainDate
}

const props = withDefaults(defineProps<Props>(), {
  channels: () => [],
})

const { n, t } = useI18n()

interface Category {
  label: string
  color: string
}

const categories: {[key: string]: Category} = {
  one_time: {label: t('enums.repeat_customer_categories.one_time'), color: '#003f5c'},
  repeat: {label: t('enums.repeat_customer_categories.repeat'), color: '#bc5090'},
}

const weekdays = {
  mon: {label: t('enums.weekdays_abbr.mon')},
  tue: {label: t('enums.weekdays_abbr.tue')},
  wed: {label: t('enums.weekdays_abbr.wed')},
  thu: {label: t('enums.weekdays_abbr.thu')},
  fri: {label: t('enums.weekdays_abbr.fri')},
  sat: {label: t('enums.weekdays_abbr.sat')},
  sun: {label: t('enums.weekdays_abbr.sun')},
}

/**
 * Raw data
 */

const loading = ref<boolean>(false)
const pendingRequestAbortControllers: AbortController[] = []

const rawData = computedAsync<null | GetTransientDriversResponse>(

  async () => {

    if (
      props.locationIds.length === 0 ||
      !props.start ||
      !props.end
    ) {
      return null
    }

    pendingRequestAbortControllers.forEach((controller) => {
      controller.abort()
    })
    pendingRequestAbortControllers.length = 0

    const abortController = new AbortController()
    pendingRequestAbortControllers.push(abortController)

    loading.value = true
    let data = null

    await ReportingApi.getTransientDrivers({
      locationIds: props.locationIds,
      start: props.start,
      end: props.end,
      signal: abortController.signal,
    })
    .then((response) => {
      data = response
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error('Couldn\'t load booking duration data!')
    })
    .finally(() => {
      loading.value = false
    })

    return data    

  },

  null // default value

)

/**
 * Aggregate chart
 */

const aggregateChartOptions: ChartOptions<'bar'> = {
  scales: {
    y: {
      min: 0,
      max: 100,
      ticks: {
        callback: (value, _index, _values) => {
          return n((value as number) / 100, {style: 'percent', maximumFractionDigits: 0})
        },
      },
    },
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<'bar'>) => {
          return n((context.raw as number) / 100, {style: 'percent', maximumFractionDigits: 0})
        },
      },
    },
  },
}

const aggregateChartData = computed<ChartData<'bar', number[]>>(() => {

  let data: number[] = []

  if (rawData.value) {

    // Step 1: Total up transactions for the selected channels
    // Step 1: Total up transactions for the selected channels

    let totalTransactions = 0
    const transactionsByCategory: {[key: string]: number} = {}

    Object.keys(categories).forEach(categoryName => {

      let categoryTransactions = 0

      const byChannel: {[key: string]: number} = rawData.value?.totals[categoryName as 'one_time' | 'repeat']?.by_channel ?? {}

      Object.entries(byChannel)
        .filter(([channelName, _channelTransactions]) => {
          return props.channels.length === 0 || props.channels.includes(channelName)
        })
        .forEach(([_channelName, channelTransactions]) => {
          categoryTransactions += channelTransactions
          totalTransactions += channelTransactions
        })

      transactionsByCategory[categoryName] = categoryTransactions

    })

    // Step 2: Divide to derive percentages

    data = Object.keys(categories).map((categoryName) => {      
      return totalTransactions ? Math.round(transactionsByCategory[categoryName] / totalTransactions * 10000) / 100 : 0
    })

  }

  return {
    labels: Object.values(categories).map((category) => category.label),
    datasets: [
      {
        data: data,
        backgroundColor: Object.values(categories).map((category) => category.color),
      },
    ],
  }

})

/**
 * "Breakdown by Day" chart
 */

const breakdownChartOptions: ChartOptions<'bar'> = {
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
      ticks: {
        callback: (value, _index, _values) => {
          return n((value as number) / 100, {style: 'percent', maximumFractionDigits: 0})
        },
      },
    },
  },
  plugins: {
    title: {
      display: true,
      text: 'Breakdown by Day'
    },
    tooltip: {
      callbacks: {
        title: (tooltipItems: TooltipItem<'bar'>[]) => {
          return `${tooltipItems[0].label} – ${tooltipItems[0].dataset.label}`
        },
        label: (context: TooltipItem<'bar'>) => {
          return n((context.raw as number) / 100, {style: 'percent', maximumFractionDigits: 0})
        },
      },
    },
  },
}

const breakdownChartData = computed<ChartData<'bar', number[]>>(() => {


  let datasets: ChartDataset<'bar'>[] = []

  if (rawData.value) {

    // Step 1: Total up transactions for the selected channels

    const weekdayTotals: {[key: string]: {total: number, by_category: {[key: string]: number}}} = {}

    Object.keys(weekdays).forEach((weekdayName) => {

      weekdayTotals[weekdayName] = {
        total: 0,
        by_category: {},
      }

      Object.keys(categories).forEach((categoryName) => {
        
        let categoryTransactions = 0

        const byChannel: {[key: string]: number} = rawData.value?.by_weekday[weekdayName][categoryName as 'one_time' | 'repeat']?.by_channel ?? {}

        Object.entries(byChannel)
          .filter(([channelName, _channelTransactions]) => {
            return props.channels.length === 0 || props.channels.includes(channelName)
          })
          .forEach(([_channelName, channelTransactions]) => {
            categoryTransactions += channelTransactions
          })

        weekdayTotals[weekdayName].total += categoryTransactions
        weekdayTotals[weekdayName].by_category[categoryName] = categoryTransactions

      })

    })

    // Step 2: Divide to derive percentages

    datasets = Object.entries(categories).map(([categoryName, category]) => {

      const data = Object.keys(weekdays).map((weekdayName) => {
        const weekdayTotal = weekdayTotals[weekdayName].total
        const categoryTotal = weekdayTotals[weekdayName].by_category[categoryName]
        return weekdayTotal ? Math.round(categoryTotal / weekdayTotal * 10000) / 100 : 0
      })

      return {
        label: category.label,
        backgroundColor: category.color,
        data: data,
      }

    })

  }

  return {
    labels: Object.values(weekdays).map((weekday) => weekday.label),
    datasets: datasets,
  }

})

/**
 * Adjust the chart size responsively
 * https://vue-chartjs.org/guide/#chart-with-dynamic-styles
 */

const aggregateChartContainer = useTemplateRef('aggregateChartContainer')
const breakdownChartContainer = useTemplateRef('breakdownChartContainer')
const { chartStyles: aggregateChartStyles } = useFitChartToContainer(aggregateChartContainer)
const { chartStyles: breakdownChartStyles } = useFitChartToContainer(breakdownChartContainer)

</script>
